import { useEffect, useMemo, useRef, useState } from 'react';
import {
  recentSentimentNews,
  useDashboardStore,
  type AgentRuntimeState,
  type RecentNewsForTooltip,
} from '@/state/store';
import {
  getPodRoomAgentRects,
  hitTestAgent,
  type AgentHitRect,
} from '@/scenes/podRoomHitRects';

/**
 * DOM overlay that paints hover-tooltips over the Pixi pod-room
 * scene.  Native ``title=`` attributes don't work on canvas content
 * (the canvas is a single DOM element with no hit-test info), so we
 * recreate hover affordances here using:
 *
 * 1. A shared registry (``podRoomHitRects``) populated by the scene
 *    on every layout, containing the circular hit area of each
 *    agent + PM.
 * 2. A ``pointermove`` listener on the host element (mounted as the
 *    overlay's parent) that translates pointer coords into a
 *    "hovered agent" decision via ``hitTestAgent``.
 * 3. A small absolute-positioned card that displays the hovered
 *    agent's role + last ambient phrase + last signal + mood +
 *    timestamps.  Reads agent state from the Zustand store, so the
 *    tooltip stays live even as new events land while the user
 *    keeps their cursor stationary.
 *
 * ``pointer-events: none`` on the overlay container so all hover /
 * click events still flow to the Pixi canvas underneath (selection,
 * future click-to-focus, etc).
 *
 * The overlay listens to pointer events on its **parent** rather
 * than itself -- otherwise ``pointer-events: none`` would silence
 * the events.  This pattern requires the parent div to have
 * ``position: relative`` (which ``PixiCanvas``'s host already does).
 */

const AGENT_TOOLTIP_WIDTH = 280;
const AGENT_TOOLTIP_OFFSET = 14;

interface TooltipState {
  rect: AgentHitRect;
  /** Pointer position in CSS pixels relative to the host element. */
  px: number;
  py: number;
}

export function PixiTooltipLayer({ selectedPodId }: { selectedPodId: string }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<TooltipState | null>(null);

  // Subscribe to the pod's agent map.  We only need agent state for
  // the *currently hovered* agent, but reading the whole map is fine
  // -- the agent objects are referentially stable across unrelated
  // store updates, so React only re-renders when the hovered agent's
  // own state changes.
  const podAgents = useDashboardStore(
    (s) => s.pods[selectedPodId]?.agents ?? {},
  );
  const podPm = useDashboardStore((s) => s.pods[selectedPodId]?.pm ?? null);
  const podPositions = useDashboardStore(
    (s) => s.pods[selectedPodId]?.positions ?? {},
  );
  const feed = useDashboardStore((s) => s.feed);

  // For the SA agent: compute the last few news headlines that
  // overlap this pod's universe so we can show them in the tooltip.
  // Cheap because ``recentSentimentNews`` is bounded by ``limit`` and
  // only scans until full.  We always compute (vs only when SA is
  // hovered) so the result is referentially stable as long as ``feed``
  // and ``podPositions`` don't change — avoiding a flash when the
  // user moves onto SA.
  const saNews = useMemo(() => {
    const tickers = Object.keys(podPositions);
    const fromSa = podAgents.sa?.topTickers ?? [];
    const merged = Array.from(new Set([...tickers, ...fromSa]));
    return recentSentimentNews(feed, merged, 5);
  }, [feed, podPositions, podAgents.sa?.topTickers]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const host = container.parentElement;
    if (!host) return;

    // Attaching to the host (parent) so our own ``pointer-events:none``
    // doesn't swallow the events.  Buying us hover-without-blocking.
    const handleMove = (event: PointerEvent) => {
      // Bail out on touch -- touch users tap to select rather than
      // hover, and a touch-and-hold tooltip overlay would block taps.
      if (event.pointerType === 'touch') return;
      const bounds = host.getBoundingClientRect();
      const px = event.clientX - bounds.left;
      const py = event.clientY - bounds.top;
      const rect = hitTestAgent(px, py);
      if (rect) {
        setHover({ rect, px, py });
      } else {
        // Only clear when we leave a previously-hovered area; saves a
        // render when the cursor moves across blank canvas space.
        setHover((prev) => (prev ? null : prev));
      }
    };

    const handleLeave = () => setHover(null);

    host.addEventListener('pointermove', handleMove);
    host.addEventListener('pointerleave', handleLeave);
    return () => {
      host.removeEventListener('pointermove', handleMove);
      host.removeEventListener('pointerleave', handleLeave);
    };
  }, []);

  // Re-run the hit-test against the freshest layout when the rect
  // registry changes (e.g. resize).  ``getPodRoomAgentRects`` is a
  // module-level getter so React can't observe it directly; we read
  // it here only to invalidate stale hover state when the underlying
  // rect for the hovered actor disappears.
  useEffect(() => {
    if (!hover) return;
    const stillExists = getPodRoomAgentRects().some(
      (r) => r.actor === hover.rect.actor,
    );
    if (!stillExists) setHover(null);
  }, [hover]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        // Critical: don't block the underlying canvas from receiving
        // events (e.g. future click-to-focus on a character).
        pointerEvents: 'none',
        zIndex: 4,
      }}
    >
      {hover ? (
        <AgentTooltip
          rect={hover.rect}
          px={hover.px}
          py={hover.py}
          agent={podAgents[hover.rect.actor] ?? null}
          pmSummary={
            hover.rect.actor === 'pm'
              ? {
                  rationale: podPm?.lastRationale ?? null,
                  uiSummary: podPm?.lastUiSummary ?? null,
                  confidence: podPm?.confidence ?? null,
                  decisionAt: podPm?.decisionAt ?? null,
                  intentsCount: podPm?.intentsCount ?? null,
                  targetGross: podPm?.targetGross ?? null,
                  thinking: podPm?.thinking ?? false,
                }
              : null
          }
          saNews={hover.rect.actor === 'sa' ? saNews : null}
        />
      ) : null}
    </div>
  );
}

/** Pretty short label for the most recent agent signal -- short
 *  enough to fit the tooltip's mono row. */
function fmtSignal(s: number | null, conf: number | null): string {
  if (s == null) return '—';
  const sign = s > 0 ? '+' : '';
  const num = `${sign}${s.toFixed(2)}`;
  if (conf == null) return num;
  return `${num} · conf ${Math.round(conf * 100)}%`;
}

function relAge(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1_000)}с назад`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}м назад`;
  return `${Math.floor(ms / 3_600_000)}ч назад`;
}

interface PmHoverSummary {
  rationale: string | null;
  uiSummary: string | null;
  confidence: number | null;
  decisionAt: string | null;
  intentsCount: number | null;
  targetGross: number | null;
  thinking: boolean;
}

function AgentTooltip({
  rect,
  px,
  py,
  agent,
  pmSummary,
  saNews,
}: {
  rect: AgentHitRect;
  px: number;
  py: number;
  agent: AgentRuntimeState | null;
  pmSummary: PmHoverSummary | null;
  /** Recent news for the SA tooltip; ``null`` for non-SA hover. */
  saNews: RecentNewsForTooltip[] | null;
}) {
  // Position the card to the right of the cursor by default, but
  // flip it to the left if we'd overflow the viewport on the right
  // edge.  Same for the vertical axis.
  const containerWidth =
    (rect && typeof window !== 'undefined' ? window.innerWidth : 1200) - 24;
  const wouldOverflowRight = px + AGENT_TOOLTIP_OFFSET + AGENT_TOOLTIP_WIDTH >
    containerWidth;
  const left = wouldOverflowRight
    ? Math.max(8, px - AGENT_TOOLTIP_OFFSET - AGENT_TOOLTIP_WIDTH)
    : px + AGENT_TOOLTIP_OFFSET;
  const top = Math.max(8, py - 8);

  const isPm = rect.actor === 'pm';

  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute',
        left,
        top,
        width: AGENT_TOOLTIP_WIDTH,
        background: '#0d1117f2',
        border: '1px solid #30363d',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(4px)',
        padding: '8px 10px',
        fontSize: 11,
        color: '#e6edf3',
        lineHeight: 1.45,
        zIndex: 5,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 6,
          marginBottom: 4,
        }}
      >
        <strong
          style={{
            color: '#e6edf3',
            fontSize: 12,
            letterSpacing: 0.2,
          }}
        >
          {rect.label}
        </strong>
        <span
          style={{
            color: '#6e7681',
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          {rect.actor}
        </span>
      </div>

      {/* PM-specific block: rationale + confidence + target gross. */}
      {isPm && pmSummary ? (
        <>
          {pmSummary.thinking ? (
            <div style={{ color: '#58a6ff', fontStyle: 'italic', marginBottom: 4 }}>
              думает (LLM call в полёте)…
            </div>
          ) : null}
          {pmSummary.uiSummary ? (
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: '#8b949e', fontSize: 9 }}>Текущий план: </span>
              <span style={{ color: '#e6edf3' }}>{pmSummary.uiSummary}</span>
            </div>
          ) : null}
          {pmSummary.rationale ? (
            <div style={{ marginBottom: 6 }}>
              <span style={{ color: '#8b949e', fontSize: 9 }}>Обоснование: </span>
              <span
                style={{
                  color: '#c9d1d9',
                  fontSize: 10,
                  display: '-webkit-box',
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {pmSummary.rationale}
              </span>
            </div>
          ) : null}
          <KeyValRow
            k="confidence"
            v={
              pmSummary.confidence != null
                ? `${Math.round(pmSummary.confidence * 100)}%`
                : '—'
            }
          />
          <KeyValRow
            k="target gross"
            v={pmSummary.targetGross != null ? pmSummary.targetGross.toFixed(2) : '—'}
          />
          <KeyValRow
            k="intents"
            v={pmSummary.intentsCount != null ? `${pmSummary.intentsCount}` : '—'}
          />
          {pmSummary.decisionAt ? (
            <KeyValRow
              k="решение"
              v={relAge(Date.now() - new Date(pmSummary.decisionAt).getTime())}
            />
          ) : null}
        </>
      ) : null}

      {/* Analyst-specific block: last ambient phrase + last signal. */}
      {!isPm ? (
        <>
          {agent?.thinking ? (
            <div style={{ color: '#58a6ff', fontStyle: 'italic', marginBottom: 4 }}>
              думает (LLM call в полёте)…
            </div>
          ) : null}
          {agent?.lastPhrase ? (
            <div style={{ marginBottom: 6 }}>
              <div style={{ color: '#8b949e', fontSize: 9, marginBottom: 2 }}>
                Последняя фраза
                {agent.lastPhraseKind ? (
                  <span style={{ color: '#6e7681' }}> · {agent.lastPhraseKind}</span>
                ) : null}
              </div>
              <div style={{ color: '#e6edf3', fontStyle: 'italic' }}>
                «{agent.lastPhrase}»
              </div>
              {agent.lastPhraseAt ? (
                <div style={{ color: '#6e7681', fontSize: 9, marginTop: 2 }}>
                  {relAge(Date.now() - new Date(agent.lastPhraseAt).getTime())}
                </div>
              ) : null}
            </div>
          ) : (
            <div style={{ color: '#6e7681', fontStyle: 'italic', marginBottom: 6 }}>
              ещё ничего не говорил
            </div>
          )}

          {agent?.lastSignal != null || (agent?.topTickers && agent.topTickers.length > 0) ? (
            <>
              <KeyValRow
                k="последний сигнал"
                v={fmtSignal(agent?.lastSignal ?? null, agent?.lastConfidence ?? null)}
              />
              {agent?.topTickers && agent.topTickers.length > 0 ? (
                <KeyValRow
                  k="top тикеры"
                  v={agent.topTickers.slice(0, 3).join(', ')}
                />
              ) : null}
            </>
          ) : null}
          {agent?.mood ? (
            <KeyValRow k="настроение" v={String(agent.mood)} />
          ) : null}
          {agent?.latencyMs != null ? (
            <KeyValRow k="latency" v={`${Math.round(agent.latencyMs)} мс`} />
          ) : null}
          {saNews ? <RecentNewsBlock items={saNews} /> : null}
        </>
      ) : null}
    </div>
  );
}

function sentimentDot(value: number): { color: string; glyph: string } {
  if (value > 0.05) return { color: '#2ea043', glyph: '▲' };
  if (value < -0.05) return { color: '#f85149', glyph: '▼' };
  return { color: '#8b949e', glyph: '•' };
}

function RecentNewsBlock({ items }: { items: RecentNewsForTooltip[] }) {
  if (items.length === 0) {
    return (
      <div
        style={{
          marginTop: 8,
          paddingTop: 6,
          borderTop: '1px solid #21262d',
          color: '#6e7681',
          fontSize: 10,
          fontStyle: 'italic',
        }}
      >
        пока тихо в новостях по нашим эмитентам
      </div>
    );
  }
  return (
    <div
      style={{
        marginTop: 8,
        paddingTop: 6,
        borderTop: '1px solid #21262d',
      }}
    >
      <div
        style={{
          color: '#8b949e',
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          marginBottom: 4,
        }}
      >
        Последние новости
      </div>
      {items.map((item) => {
        const dot = sentimentDot(item.sentiment);
        const ticker = item.tickers[0] ?? '';
        return (
          <div
            key={item.id}
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto auto 1fr',
              gap: 6,
              alignItems: 'baseline',
              fontSize: 10,
              padding: '2px 0',
              lineHeight: 1.3,
            }}
          >
            <span style={{ color: dot.color, fontSize: 9 }}>{dot.glyph}</span>
            {ticker ? (
              <span
                style={{
                  fontFamily: 'monospace',
                  color: dot.color,
                  fontSize: 9,
                  padding: '0 4px',
                  borderRadius: 6,
                  border: `1px solid ${dot.color}`,
                }}
              >
                {ticker}
              </span>
            ) : (
              <span />
            )}
            <span
              style={{
                color: '#c9d1d9',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={`${item.title} (sentiment ${item.sentiment.toFixed(2)})`}
            >
              {item.title}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function KeyValRow({ k, v }: { k: string; v: string }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 6,
        marginTop: 1,
        fontSize: 10,
      }}
    >
      <span style={{ color: '#8b949e' }}>{k}</span>
      <span style={{ color: '#e6edf3', fontVariantNumeric: 'tabular-nums' }}>
        {v}
      </span>
    </div>
  );
}
